// nebula/comp.js: full-res COMPOSITE (bicubic volume upsample, crisp stars, the new star's
// PSF with JWST-style 6+2 spikes, ignition burst) and MOTES (tiny foreground dust glints).
import { COMMON } from './glsl.js';

export const COMPOSITE = /* glsl */`
precision highp float;
uniform sampler2D uVolT, uVis; uniform vec2 uVolTexel;
uniform mat3 uCamMat; uniform vec2 uTan, uShift;
uniform vec3 uCoreCol; uniform float uCoreI, uCoreR, uSpike, uSpikeL, uHaloI, uHaloR, uVisMix;
uniform float uStarsI, uFgStars, uPxA, uTime, uDpr;
uniform vec3 uFlashCol; uniform float uFlash, uFlashR;
uniform vec2 uStarPx; uniform float uStarOn, uSoft;
precision highp sampler3D;
uniform sampler3D uDet; uniform float uDetN, uRefD, uRefR, uRimS;
uniform vec3 uCamPos, uFwd, uLDirW, uStarW; uniform vec2 uLDirS, uEdgeR;
uniform vec4 uEdge;    // full-res edge pass: strength, noise scale (tiles / world unit), sharpen half-width, rim gain
uniform vec4 uEdge2;   // luminance-guide mix, streak stretch 0..0.9, radial streaks (brand), flat-area smoothing (phones)
uniform vec4 uEdge3;   // warp amplitude (texels), warp bias, threshold-noise gain, -
#if R8
uniform vec4 uRimM; uniform float uVisN; uniform vec4 uHaloC;   // r8 (R8=1 lit-ignite program): magenta lit-side rims, neutral star colour, teal push-in halo
#endif
#if R9
uniform vec4 uR9C, uR9D; uniform vec3 uR9U;   // uR9D: dome wisps amount, radius (px)   // r9 (R9=1 lit program): rim gain, flat-pink -> umber, tap (texels), star fade (px); umber / its luminance
#endif
uniform vec4 uEdge4;   // interior striation amount, lit-face luminance scale, clear radius around the star (px, 0 = off), -
varying vec2 vUv;
${COMMON}
// Catmull-Rom (sharp) bicubic, 9 bilinear taps
vec4 catmull(sampler2D tex, vec2 uv, vec2 texel) {
  vec2 sp = uv / texel;
  vec2 t1 = floor(sp - 0.5) + 0.5;
  vec2 f = sp - t1;
  vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f));
  vec2 w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
  vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f));
  vec2 w3 = f * f * (-0.5 + 0.5 * f);
  vec2 w12 = w1 + w2; vec2 o12 = w2 / w12;
  vec2 p0 = (t1 - 1.0) * texel, p3 = (t1 + 2.0) * texel, p12 = (t1 + o12) * texel;
  vec4 r = texture2D(tex, vec2(p0.x, p0.y)) * w0.x * w0.y;
  r += texture2D(tex, vec2(p12.x, p0.y)) * w12.x * w0.y;
  r += texture2D(tex, vec2(p3.x, p0.y)) * w3.x * w0.y;
  r += texture2D(tex, vec2(p0.x, p12.y)) * w0.x * w12.y;
  r += texture2D(tex, vec2(p12.x, p12.y)) * w12.x * w12.y;
  r += texture2D(tex, vec2(p3.x, p12.y)) * w3.x * w12.y;
  r += texture2D(tex, vec2(p0.x, p3.y)) * w0.x * w3.y;
  r += texture2D(tex, vec2(p12.x, p3.y)) * w12.x * w3.y;
  r += texture2D(tex, vec2(p3.x, p3.y)) * w3.x * w3.y;
  return max(r, 0.0);
}
vec4 bspline(sampler2D tex, vec2 uv, vec2 texel) {
  vec2 sp = uv / texel - 0.5;
  vec2 i = floor(sp), f = sp - i;
  vec2 f2 = f * f, f3 = f2 * f;
  vec2 w0 = (1.0 - 3.0 * f + 3.0 * f2 - f3) / 6.0;
  vec2 w1 = (4.0 - 6.0 * f2 + 3.0 * f3) / 6.0;
  vec2 w2 = (1.0 + 3.0 * f + 3.0 * f2 - 3.0 * f3) / 6.0;
  vec2 w3 = f3 / 6.0;
  vec2 g0 = w0 + w1, g1 = w2 + w3;
  vec2 h0 = (w1 / g0 - 1.0 + i + 0.5) * texel, h1 = (w3 / g1 + 1.0 + i + 0.5) * texel;
  return g0.y * (g0.x * texture2D(tex, vec2(h0.x, h0.y)) + g1.x * texture2D(tex, vec2(h1.x, h0.y)))
       + g1.y * (g0.x * texture2D(tex, vec2(h0.x, h1.y)) + g1.x * texture2D(tex, vec2(h1.x, h1.y)));
}
// stellar colours: mostly white, some warm (K/M), a few blue-white (B/A); kept subtle
vec3 starTint(float h) {
  vec3 c = mix(vec3(1.0, 0.62, 0.38), vec3(1.0, 0.86, 0.72), smoothstep(0.0, 0.25, h));
  c = mix(c, vec3(0.96, 0.96, 1.0), smoothstep(0.3, 0.55, h));
  return mix(c, vec3(0.7, 0.8, 1.0), smoothstep(0.75, 1.0, h));
}
// JWST PSF spikes: 3 bright axes (vertical, +-60 deg) and a faint horizontal one
float spikes(vec2 d, float len, float wid) {
  float s = 0.0;
  for (int k = 0; k < 4; k++) {
    float an = k == 0 ? 1.5708 : (k == 1 ? 0.5236 : (k == 2 ? 2.618 : 0.0));
    vec2 dir = vec2(cos(an), sin(an));
    float al = abs(dot(d, dir)); float pe = abs(dot(d, vec2(-dir.y, dir.x)));
    float w = k < 3 ? 1.0 : 0.3;
    s += w * exp(-sq(pe / (wid * (1.0 + al * 0.01)))) * exp(-al / len);
  }
  return s;
}
// crisp analytic stars on a cube-mapped grid, 3 layers. Flux-normalised gaussian PSF
// (sigma ~0.6 device px) so points stay 1-2 px and don't shimmer when the camera moves.
vec3 stars(vec3 rd, float T, out vec3 fg) {
  fg = vec3(0.0);
  vec3 a = abs(rd); vec2 uv; float face;
  if (a.x > a.y && a.x > a.z) { uv = rd.yz / a.x; face = rd.x > 0.0 ? 0.0 : 1.0; }
  else if (a.y > a.z) { uv = rd.xz / a.y; face = rd.y > 0.0 ? 2.0 : 3.0; }
  else { uv = rd.xy / a.z; face = rd.z > 0.0 ? 4.0 : 5.0; }
  vec3 col = vec3(0.0);
  float sig = max(0.62, 0.5 * uDpr);
  float norm = 1.0 / (6.2832 * sig * sig);
  for (int L = 0; L < 3; L++) {
    float fl = float(L);
    float sc = L == 0 ? 9.0 : (L == 1 ? 38.0 : 140.0);
    vec2 g = uv * sc; vec2 id = floor(g); vec2 f = fract(g);
    vec3 h = hash33n(vec3(id, face * 17.0 + fl * 131.0));
    float keep = L == 0 ? 0.3 : (L == 1 ? 0.5 : 0.6);
    if (h.z >= keep) continue;
    vec2 sp = 0.25 + 0.5 * h.xy;
    vec2 d = (f - sp) / (sc * uPxA);          // device pixels
    float u = clamp(1.0 - h.z / keep, 0.0, 1.0);
    float u2 = u * u, u4 = u2 * u2;
    // flux: steep luminosity function; only a handful of layer-0 stars are bright
    float F = L == 0 ? (0.6 + 26.0 * u4 * u2) : (L == 1 ? 0.25 + 2.2 * u4 : 0.09 + 0.4 * u2);
    float r2 = dot(d, d);
    float s = F * norm * exp(-0.5 * r2 / (sig * sig));
    if (L == 0) {
      float m = u4 * u2;
      if (m > 0.03 && r2 < sq(40.0 * uDpr)) s += F * 0.012 * m * spikes(d / uDpr, 2.5 + 6.0 * m, 0.55) + F * 0.004 * m / (1.0 + r2 / sq(3.0 * uDpr));
    }
    vec3 c = starTint(fract(h.x * 7.3 + h.y * 3.1));
    float tw = 1.0 + 0.12 * sin(uTime * (1.3 + 3.0 * h.x) + h.y * 40.0) * (L == 0 ? 1.0 : 0.0);
    vec3 v = c * s * tw;
    if (fract(h.x * 13.7 + h.y * 5.3) < uFgStars) fg += v * 0.5;
    else col += v;
  }
  // stars behind dust: dimmed and reddened
  return col * pow(vec3(T), vec3(0.8, 1.0, 1.3));
}
vec4 dec(vec4 s) {
#if LDR
  s.rgb = s.rgb / max(1.0 - s.rgb, 0.004);
#endif
  return s;
}
float lumi(vec3 c) { return dot(c, vec3(0.3, 0.5, 0.2)); }
#if R9
float pk9(vec3 c) { return max(min(c.r, c.b) - c.g, 0.0); }   // magenta content (rust, teal, azure: 0)
#endif
// full-res filament noise: ridged fbm from the detail volume on a plane through the subject
// (moves with the cloud, not the screen), stretched along the cluster light (ignite: the
// photo-evaporation streaks point at it) or radially from the star (brand)
float edgeNoise(vec3 rd) {
  vec3 pw = uCamPos + rd * (uRefD / max(dot(rd, uFwd), 0.2));
  vec3 pa;
  if (uEdge2.z > 0.5) {
    vec3 q = pw - uStarW; float r = max(length(q), 1e-3);
    pa = uStarW + q * ((uRefR + (r - uRefR) * (1.0 - uEdge2.y)) / r);
  } else pa = pw - uLDirW * dot(pw, uLDirW) * uEdge2.y;
  vec3 x = pa * uEdge.y;
  float lod = log2(max(uRefD * uPxA * uEdge.y * uDetN, 1e-4));
  vec4 a = textureLod(uDet, x, max(lod, 0.0));
  float b = textureLod(uDet, x * 2.71 + a.gba * 0.06 + 0.37, max(lod + 1.44, 0.0)).b;
  return a.b * 0.55 + b * 0.45;
}
void main() {
  vec2 ndc = vUv * 2.0 - 1.0 - uShift * 2.0;
  vec3 rd = normalize(uCamMat * vec3(ndc * uTan, -1.0));
  vec2 dp = (gl_FragCoord.xy - uStarPx) / uDpr;
  float dpx = length(dp);
  vec2 uvS = vUv;
#if EDGE
  // EDGE PASS (full res). 1) a wide probe (+-3 texels) of the march's transmittance (+ luminance
  // for emission-lit brand shells) finds silhouettes; 2) there the lookup is warped along the
  // light by streak noise anchored in the cloud, tearing the edge into wisps that point at the
  // light; 3) at the warped point a joint-bilateral re-threshold sharpens the edge to ~1-2 px
  // and adds a razor rim on the lit side.
  float E = 0.0, nE = 0.5;
  vec2 ld = normalize(mix(uLDirS, -dp / max(dpx, 1e-3), uRimS) + 1e-4);
  if (uEdge.x > 0.0) {
    vec2 tw = uVolTexel * 3.0;
    vec4 w1 = dec(texture2D(uVolT, vUv - tw)), w4 = dec(texture2D(uVolT, vUv + tw));
    vec4 w2 = dec(texture2D(uVolT, vUv + vec2(tw.x, -tw.y))), w3 = dec(texture2D(uVolT, vUv + vec2(-tw.x, tw.y)));
    float m1 = lumi(w1.rgb), m2 = lumi(w2.rgb), m3 = lumi(w3.rgb), m4 = lumi(w4.rgb);
    float mm = (m1 + m2 + m3 + m4) * 0.25 + 1e-4, kw = uEdge2.x;
    float h1 = mix(w1.a, mm / (m1 + mm), kw), h2 = mix(w2.a, mm / (m2 + mm), kw);
    float h3 = mix(w3.a, mm / (m3 + mm), kw), h4 = mix(w4.a, mm / (m4 + mm), kw);
    float gw = max(max(h1, h2), max(h3, h4)) - min(min(h1, h2), min(h3, h4));
    E = smoothstep(0.04, 0.22, gw) * uEdge.x * smoothstep(uEdgeR.x, uEdgeR.y, dpx);
    if (E > 0.003) {
      nE = edgeNoise(rd);
      uvS = vUv - ld * uVolTexel * (uEdge3.x * E * (nE - uEdge3.y));
    }
  }
#endif
#if SHARP
  vec4 v = uSoft > 0.5 ? bspline(uVolT, uvS, uVolTexel) : catmull(uVolT, uvS, uVolTexel);
#else
  vec4 v = bspline(uVolT, uvS, uVolTexel);
#endif
  v = dec(v);
  vec3 c = v.rgb;
#if EDGE
  if (E > 0.003) {
    vec2 tx = uVolTexel * 1.5;
    vec4 s1 = dec(texture2D(uVolT, uvS - tx)), s4 = dec(texture2D(uVolT, uvS + tx));
    vec4 s2 = dec(texture2D(uVolT, uvS + vec2(tx.x, -tx.y))), s3 = dec(texture2D(uVolT, uvS + vec2(-tx.x, tx.y)));
    float l0 = lumi(c), l1 = lumi(s1.rgb), l2 = lumi(s2.rgb), l3 = lumi(s3.rgb), l4 = lumi(s4.rgb);
    float lm = (l0 + l1 + l2 + l3 + l4) * 0.2 + 1e-4, kg = uEdge2.x;
    float g0 = mix(v.a, lm / (l0 + lm), kg), g1 = mix(s1.a, lm / (l1 + lm), kg), g2 = mix(s2.a, lm / (l2 + lm), kg);
    float g3 = mix(s3.a, lm / (l3 + lm), kg), g4 = mix(s4.a, lm / (l4 + lm), kg);
    float gmin = min(g0, min(min(g1, g2), min(g3, g4))), gmax = max(g0, max(max(g1, g2), max(g3, g4)));
    float rng = gmax - gmin;
    float Es = E * smoothstep(0.03, 0.15, rng);
    float x = (g0 - gmin) / max(rng, 1e-4);
    float xs = smoothstep(0.5 - uEdge.z, 0.5 + uEdge.z, x - uEdge3.z * (nE - 0.45));
    float gt = gmin + rng * xs, sg = 0.2 * rng + 1e-3;
    float w0 = 0.7 * exp(-sq((g0 - gt) / sg)), e1 = exp(-sq((g1 - gt) / sg)), e2 = exp(-sq((g2 - gt) / sg));
    float e3 = exp(-sq((g3 - gt) / sg)), e4 = exp(-sq((g4 - gt) / sg));
    vec4 bl = (vec4(c, v.a) * w0 + s1 * e1 + s2 * e2 + s3 * e3 + s4 * e4) / (w0 + e1 + e2 + e3 + e4 + 1e-5);
    vec2 gd = vec2(g2 + g4 - g1 - g3, g3 + g4 - g1 - g2);
    float face = smoothstep(-0.3, 0.7, dot(gd / (length(gd) + 1e-4), ld));
    vec3 rc = c; float lb = l0;   // rim colour: the brightest neighbour (lit front or backlit glow)
    if (l1 > lb) { lb = l1; rc = s1.rgb; } if (l2 > lb) { lb = l2; rc = s2.rgb; }
    if (l3 > lb) { lb = l3; rc = s3.rgb; } if (l4 > lb) { lb = l4; rc = s4.rgb; }
    float band = 4.0 * xs * (1.0 - xs);
#if R8
    vec3 rcM = mix(rc, uRimM.rgb * (lumi(rc) * 2.4 + 0.015), uRimM.w * face);   // r8: the star-facing rim glows magenta
    c = mix(c, bl.rgb, Es) + rcM * (band * band * face * uEdge.w * Es);
#else
    c = mix(c, bl.rgb, Es) + rc * (band * band * face * uEdge.w * Es);
#endif
    v.a = mix(v.a, bl.a, Es);
  } else if (max(uEdge2.w, uEdge3.w * (1.0 - smoothstep(200.0, 360.0, dpx))) > 0.003) {   // phones: calm the per-step dither in flat areas; r7: + near the lit star (uEdge3.w)
    vec2 tx = uVolTexel * 0.75;
    vec3 sm = dec(texture2D(uVolT, vUv - tx)).rgb + dec(texture2D(uVolT, vUv + tx)).rgb
            + dec(texture2D(uVolT, vUv + vec2(tx.x, -tx.y))).rgb + dec(texture2D(uVolT, vUv + vec2(-tx.x, tx.y))).rgb;
    c = mix(c, sm * 0.25, max(uEdge2.w, uEdge3.w * (1.0 - smoothstep(200.0, 360.0, dpx))));
  }
  // full-res striations inside the cloud: a mean-preserving multiplier from the same streak noise
  // (fibres along the light on lit cliff faces, radial spokes in brand shells); dark cores stay calm
  if (uEdge4.x > 0.0) {
    float D = smoothstep(0.3, 0.85, 1.0 - v.a) * uEdge4.x * (uEdge4.z > 0.0 ? smoothstep(uEdge4.z * 0.3, uEdge4.z, dpx) : 1.0);
    if (D > 0.003 && uEdge4.w > 0.0) {   // r7: low-frequency amplitude patches (no uniform 'hair')
      vec3 pwL = uCamPos + rd * (uRefD / max(dot(rd, uFwd), 0.2));
      D *= mix(1.0, 0.15 + 1.35 * smoothstep(0.35, 0.68, textureLod(uDet, pwL * 0.07 + vec3(0.37, 0.11, 0.73), 2.0).g), uEdge4.w);
    }
    if (D > 0.003) {
      float n = E > 0.003 ? nE : edgeNoise(rd);
      float lc = lumi(c);
      c *= 1.0 + D * (0.3 + 0.7 * smoothstep(0.02, uEdge4.y, lc)) * (n - 0.47) * 1.6;
    }
  }
#endif
#if R9
  if (uR9C.x + uR9C.y > 0.0) {
    // r9: crisp ionisation rims. The skins reach the composite as a smooth field (march res, bicubic): draw a thin
    // anti-aliased line (fwidth, ~1-2 px) on the iso-line at half the local pink peak, only on the star-facing flank
    // (taps +-uR9C.z texels toward / away from the star), warm pixels only; the flat interior of each pink skin turns umber
    float p0 = pk9(c), l0 = lumi(c) + 1e-4, fw = fwidth(p0);
    if (p0 > 0.01 * l0) {
      vec2 o9 = normalize(mix(uLDirS, -dp / max(dpx, 1e-3), uRimS) + 1e-4) * uVolTexel * uR9C.z;
      float pT = pk9(dec(texture2D(uVolT, uvS + o9)).rgb), pA = pk9(dec(texture2D(uVolT, uvS - o9)).rgb);
      float pm = max(p0, max(pT, pA)) + 1e-5;
      float fz = smoothstep(uR9C.w * 0.4, uR9C.w, dpx) * (1.0 - smoothstep(0.75, 0.98, v.a)) * smoothstep(0.02, 0.08, pm / l0);
      float dv = p0 - 0.5 * pm;
      float kE = exp(-sq(dv / (fw + 1e-6))) * smoothstep(0.05, 0.35, (pA - pT) / pm) * fz * (1.0 - 0.8 * smoothstep(0.0, 0.1, (c.b - c.r) / l0));
      c = mix(c, uR9U * l0, uR9C.y * smoothstep(0.05, 0.3, p0 / l0) * (1.0 - smoothstep(0.35, 0.85, v.a)) * (1.0 - kE) * fz);
      c += (0.7 * pm + 0.3 * l0) * kE * uR9C.x * uRimM.rgb;
    }
  }
  if (uR9D.x > 0.0 && dpx < uR9D.y) {
    // r9: the dust dome behind the star: full-res wisps (streak noise, ~mean-preserving) instead of a smooth slate blob;
    // bluish dust pixels only (open sky, v.a ~ 1, and the rust cliffs keep their look)
    float dm = uR9D.x * (1.0 - smoothstep(0.55 * uR9D.y, uR9D.y, dpx)) * smoothstep(0.08, 0.3, dpx / uR9D.y) * smoothstep(0.0, 0.06, c.b - c.r) * (1.0 - smoothstep(0.85, 1.0, v.a));
    if (dm > 0.003) {
      float nw = smoothstep(0.3, 0.72, edgeNoise(rd)), lc = lumi(c);
      c = mix(c, max(mix(vec3(lc), c, 1.2), 0.0) * (0.55 + 0.9 * nw), dm);
    }
  }
#endif
  if (uStarsI > 0.001 && v.a > 0.015) { vec3 fg; vec3 st = stars(rd, clamp(v.a, 0.0, 1.0), fg); c += (st + fg) * uStarsI; }
  if (uStarOn > 0.0) {
    vec3 vis = mix(texture2D(uVis, vec2(0.5)).rgb, vec3(1.0), uVisMix);
#if R8
    vis = mix(vis, vec3(dot(vis, vec3(0.3333))), uVisN);   // r8: the star stays blue-white behind the dust
#endif
    float core = exp(-sq(dpx / uCoreR)) + 0.12 * exp(-dpx / (uCoreR * 1.5));
    float halo = uHaloI / (1.0 + sq(dpx / uHaloR));
    float spk = dpx < uSpikeL * 7.0 ? uSpike * spikes(dp, uSpikeL, 0.75) : 0.0;
    c += uCoreCol * vis * uCoreI * (core + halo + spk);
#if R8
    if (uHaloC.w > 0.0) c += (uHaloC.rgb - uCoreCol) * vis * uCoreI * halo * uHaloC.w * smoothstep(0.4 * uHaloR, 2.0 * uHaloR, dpx);   // r8: teal push-in halo tail
#endif
  }
  // ignition burst: a radial blast of light from the star (bloom does the rest), never flat white
  c += uFlashCol * uFlash * (400.0 / (1.0 + dpx / 2.0) / (1.0 + dpx / 12.0) + 1.2 / (1.0 + sq(dpx / uFlashR)));
#if R8
  if (uHaloC.w > 0.0) c += (uHaloC.rgb - uFlashCol) * uFlash * 1.2 / (1.0 + sq(dpx / uFlashR)) * uHaloC.w * 0.6 * smoothstep(0.2 * uFlashR, uFlashR, dpx);   // r8: and the wide push-in glow
#endif
  if (any(isnan(c)) || any(isinf(c))) c = vec3(0.0);
  gl_FragColor = vec4(min(c, vec3(6.0e4)), 1.0);
}`;

/* ---------------- foreground motes: tiny, dim, sparse dust glints (stateless) ---------------- */
export const MOTES_V = /* glsl */`
attribute float aSize;
uniform float uTime, uL, uPx, uStarL, uCool;
uniform vec3 uStarW, uCamW, uWarmC, uCoolC;
uniform vec2 uShift;
varying vec3 vC; varying float vA;
void main() {
  vec3 drift = vec3(0.021, 0.009, -0.013) * uTime * (0.4 + aSize);
  vec3 wp = uCamW + (fract(position + (drift - uCamW) / uL) - 0.5) * uL;
  vec4 mv = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_Position.xy += uShift * 2.0 * gl_Position.w;
  float dist = -mv.z;
  float fade = (1.0 - smoothstep(uL * 0.3, uL * 0.5, length(wp - uCamW))) * smoothstep(0.6, 1.6, dist);
  float rs = length(wp - uStarW);
  vC = uCool * uCoolC + uWarmC * uStarL / (1.0 + rs * rs * 0.25);
  vA = fade * (0.3 + 0.7 * aSize);
  gl_PointSize = clamp(uPx * (1.0 + 1.5 * aSize) * 1.6 / max(dist, 0.1), 1.0, 2.6 * uPx);
}`;
export const MOTES_F = /* glsl */`
varying vec3 vC; varying float vA;
void main() {
  vec2 q = gl_PointCoord * 2.0 - 1.0; float r2 = dot(q, q);
  if (r2 > 1.0) discard;
  gl_FragColor = vec4(vC * vA * (1.0 - r2), 1.0);
}`;
